// The single list of property types. Forms store `value`; filters, quick chips
// and labels read from here, so a listing saved in one place is found in another.

export const PROPERTY_TYPES = [
  { value: 'Condo', en: 'Condo', fr: 'Condo' },
  { value: 'Single-Family Home', en: 'House', fr: 'Maison' },
  { value: 'Townhouse', en: 'Townhouse', fr: 'Maison de ville' },
  { value: 'Multi-Family', en: 'Plex / Multi-family', fr: 'Plex / Multilogement' },
  { value: 'Penthouse', en: 'Penthouse', fr: 'Penthouse' },
  { value: 'Land', en: 'Land', fr: 'Terrain' },
  { value: 'Commercial', en: 'Commercial', fr: 'Commercial' },
] as const;

export type PropertyTypeValue = (typeof PROPERTY_TYPES)[number]['value'];

// Grouping used by the "Houses" and "Condos" quick filters.
export const HOUSE_TYPES: PropertyTypeValue[] = ['Single-Family Home', 'Townhouse', 'Multi-Family'];
export const CONDO_TYPES: PropertyTypeValue[] = ['Condo', 'Penthouse'];

// A plot or a commercial unit has no bedrooms or bathrooms: the form does not ask, and the number stays 0.
export const ROOMLESS_TYPES: PropertyTypeValue[] = ['Land', 'Commercial'];
export const isRoomlessType = (value: unknown): boolean => ROOMLESS_TYPES.includes(value as PropertyTypeValue);

const ALIASES: Record<string, PropertyTypeValue> = {
  condo: 'Condo',
  condominium: 'Condo',
  apartment: 'Condo',
  appartement: 'Condo',
  house: 'Single-Family Home',
  maison: 'Single-Family Home',
  'single-family home': 'Single-Family Home',
  'single family home': 'Single-Family Home',
  'single-family': 'Single-Family Home',
  'luxury villa': 'Single-Family Home',
  villa: 'Single-Family Home',
  townhouse: 'Townhouse',
  'maison de ville': 'Townhouse',
  'multi-family': 'Multi-Family',
  'multi family': 'Multi-Family',
  plex: 'Multi-Family',
  duplex: 'Multi-Family',
  triplex: 'Multi-Family',
  multiplex: 'Multi-Family',
  penthouse: 'Penthouse',
  land: 'Land',
  terrain: 'Land',
  lot: 'Land',
  commercial: 'Commercial',
};

export function normalizePropertyType(raw: unknown): PropertyTypeValue {
  const key = String(raw ?? '').trim().toLowerCase();
  return ALIASES[key] || 'Single-Family Home';
}

/** The type a word names, or null: an import says so rather than guessing a house. */
export function knownPropertyType(raw: unknown): PropertyTypeValue | null {
  return ALIASES[String(raw ?? '').trim().toLowerCase()] ?? null;
}

export function isPropertyType(raw: unknown): raw is PropertyTypeValue {
  return PROPERTY_TYPES.some((t) => t.value === raw);
}

export function propertyTypeLabel(raw: unknown, lang: string): string {
  if (!raw) return '';
  const value = isPropertyType(raw) ? raw : normalizePropertyType(raw);
  const entry = PROPERTY_TYPES.find((t) => t.value === value);
  return entry ? (lang === 'fr' ? entry.fr : entry.en) : String(raw);
}
